// ENT-3137 WP1 (VETO C1): secret access tokens ride in the query string of the
// public TeleLens routes (`?token=...`). Those URLs otherwise leak verbatim to
// Amplitude autocapture, the browser address bar, the `Referer` header, and
// Sentry error/transaction payloads. This module is the single canonical
// redactor for those sensitive query parameters, shared by the address-bar
// hardening hook and the Sentry scrub so the deny-list never drifts.

// Query keys that carry bearer-grade secrets on public link routes. `token` is
// the TeleLens supplier/briefing access token; `verify` covers signed
// email-verification / confirmation links that follow the same pattern;
// `token_hash` is the single-use GoTrue invite/magic-link token on the team-join
// acceptance routes (ENT-4086) — it must never reach Amplitude, the Referer
// header, or Sentry Replay/error payloads.
export const SENSITIVE_QUERY_KEYS = ['token', 'verify', 'token_hash'] as const;

// ENT-5889 (E-03): the runtime-advisory email CTA carries a signed visit token as `?t=` on
// `/advisories/email/:id` and the customer, MSP and supplier detail routes. `t` is too generic a key to strip on every
// route, so it is redacted only on those advisory paths.
export const ADVISORY_CTA_TOKEN_KEY = 't';
const ADVISORY_CTA_PATH_RE = /^(?:(?:\/vendor|\/msp)?\/advisories\/[^/]+|\/advisories\/email\/[^/]+)\/?$/;

/** True when `pathname` is a runtime-advisory page that can carry the `?t=` CTA token. */
export function isAdvisoryCtaPath(pathname: string): boolean {
  return ADVISORY_CTA_PATH_RE.test(pathname);
}

function pathnameOf(base: string): string {
  if (base.startsWith('/')) return base;
  try {
    return new URL(base).pathname;
  } catch {
    return base;
  }
}

function stripSensitiveKeys(params: URLSearchParams, keys: readonly string[]): boolean {
  let changed = false;
  for (const key of keys) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  return changed;
}

/**
 * Remove sensitive query parameters from a full or relative URL string,
 * preserving every other parameter, the path, and any hash fragment. Returns
 * the input unchanged when there is no query string or nothing sensitive to
 * strip, so it is safe to call on route patterns and non-URL transaction names.
 *
 * `keys` defaults to every sensitive key. Callers may pass a narrower list to
 * strip only specific keys — ENT-4086 R15-A: the pre-init scrubber removes a
 * token from the URL only after it was actually persisted to session storage,
 * so it passes exactly the keys it retained.
 */
export function redactSensitiveUrlParams(
  input: string,
  keys: readonly string[] = SENSITIVE_QUERY_KEYS,
): string {
  if (typeof input !== 'string' || input.length === 0) {
    return input;
  }

  const hashIndex = input.indexOf('#');
  const hash = hashIndex >= 0 ? input.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? input.slice(0, hashIndex) : input;

  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) {
    return input;
  }

  const base = withoutHash.slice(0, queryIndex);
  const params = new URLSearchParams(withoutHash.slice(queryIndex + 1));
  // The default (every sensitive key) also covers the advisory CTA token on advisory paths.
  const effectiveKeys = keys === SENSITIVE_QUERY_KEYS && isAdvisoryCtaPath(pathnameOf(base))
    ? [...keys, ADVISORY_CTA_TOKEN_KEY]
    : keys;
  if (!stripSensitiveKeys(params, effectiveKeys)) {
    return input;
  }

  const nextQuery = params.toString();
  return `${base}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
}

/**
 * Redact sensitive keys from a bare query string (Sentry `request.query_string`
 * form), with or without a leading `?`. Pass the request's URL or path so the advisory CTA
 * token (`t`) is also stripped on advisory routes (ENT-5889).
 */
export function redactSensitiveQueryString(query: string, pathname?: string): string {
  if (typeof query !== 'string' || query.length === 0) {
    return query;
  }

  const hasPrefix = query.startsWith('?');
  const params = new URLSearchParams(hasPrefix ? query.slice(1) : query);
  const keys = pathname && isAdvisoryCtaPath(pathnameOf(pathname))
    ? [...SENSITIVE_QUERY_KEYS, ADVISORY_CTA_TOKEN_KEY]
    : SENSITIVE_QUERY_KEYS;
  if (!stripSensitiveKeys(params, keys)) {
    return query;
  }

  const next = params.toString();
  if (!next) {
    return hasPrefix ? '' : '';
  }
  return hasPrefix ? `?${next}` : next;
}

// ENT-6550: telemetry sees URLs the app never rewrites. The browser's navigation entry keeps the
// loaded URL after the address bar is cleaned, and it carries more than the public-link keys: the
// auth redirect's session tokens and the share routes' path tokens. These helpers are for outbound
// telemetry only, because stripping these keys from the live address bar would break sign-in.
// `t` is only sensitive on advisory routes, but a query field often arrives without its path and
// the router ignores case, so telemetry drops it everywhere.
const TELEMETRY_QUERY_KEYS = [
  ...SENSITIVE_QUERY_KEYS,
  ADVISORY_CTA_TOKEN_KEY,
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'code',
] as const;
const SHARE_PATH_TOKEN_RE = /(\/share\/(?:questionnaires|customer-briefings|prospect-briefings)\/)[^/?#\s]+/gi;

/** A fragment holding `key=value` pairs may be an auth redirect, so all of it goes; an anchor stays. */
export function redactFragmentForTelemetry(fragment: string): string {
  return fragment.includes('=') ? '' : fragment;
}

/** Bare query string form of `redactUrlForTelemetry`, with or without a leading `?`. */
export function redactQueryForTelemetry(query: string): string {
  const hasPrefix = query.startsWith('?');
  const params = new URLSearchParams(hasPrefix ? query.slice(1) : query);
  if (!stripSensitiveKeys(params, TELEMETRY_QUERY_KEYS)) {
    return query;
  }
  const next = params.toString();
  return next && hasPrefix ? `?${next}` : next;
}

/** Removes every bearer value we know of from a URL or path bound for telemetry. */
export function redactUrlForTelemetry(input: string): string {
  if (typeof input !== 'string' || input.length === 0) {
    return input;
  }
  const hashIndex = input.indexOf('#');
  const withoutFragment = hashIndex < 0
    ? input
    : `${input.slice(0, hashIndex)}${redactFragmentForTelemetry(input.slice(hashIndex))}`;
  return redactSensitiveUrlParams(withoutFragment, TELEMETRY_QUERY_KEYS)
    .replace(SHARE_PATH_TOKEN_RE, '$1:shareToken');
}
